import { createEffect, createMemo, getOwner, onCleanup, runWithOwner, splitProps, untrack } from "solid-js/dist/solid.js";
import { createSlotNode, createElement, insert, spread } from "../reconciler.js";
import { useRenderer } from "./hooks.js";

/**
 * Renders components somewhere else in the DOM
 *
 * Useful for inserting modals and tooltips outside of an cropping layout. If no mount point is given, the portal is inserted on the root renderable; it is wrapped in a `<box>`
 *
 * @description https://docs.solidjs.com/reference/components/portal
 */
export function Portal(props) {
  const renderer = useRenderer();
  const marker = createSlotNode(),
    mount = () => props.mount || renderer.root,
    owner = getOwner();
  let content;
  createEffect(() => {
    // basically we backdoor into a sort of renderEffect here
    content || (content = runWithOwner(owner, () => createMemo(() => props.children)));
    const el = mount();
    const container = createElement("box"),
      renderRoot = container;
    Object.defineProperty(container, "_$host", {
      get() {
        return marker.parent;
      },
      configurable: true
    });
    insert(renderRoot, content);
    el.add(container);
    props.ref && props.ref(container);
    onCleanup(() => el.remove(container.id));
  }, undefined, {
    render: true
  });
  // The reconciler consumes this marker as the runtime representation of the portal JSX node.
  return marker;
}
/**
 * Renders an arbitrary component or element with the given props
 *
 * This is a lower level version of the `Dynamic` component, useful for
 * performance optimizations in libraries. Do not use this unless you know
 * what you are doing.
 * ```typescript
 * const element = () => multiline() ? 'textarea' : 'input';
 * createDynamic(element, { value: value() });
 * ```
 * @description https://docs.solidjs.com/reference/components/dynamic
 */
export function createDynamic(component, props) {
  const cached = createMemo(component);
  return createMemo(() => {
    const component = cached();
    switch (typeof component) {
      case "function":
        // if (isDev) Object.assign(component, { [$DEVCOMP]: true })
        return untrack(() => component(props));
      case "string":
        const el = createElement(component);
        spread(el, props);
        return el;
      default:
        break;
    }
  });
}

/**
 * Renders an arbitrary custom or native component and passes the other props
 * ```typescript
 * <Dynamic component={multiline() ? 'textarea' : 'input'} value={value()} />
 * ```
 * @description https://docs.solidjs.com/reference/components/dynamic
 */
export function Dynamic(props) {
  const [, others] = splitProps(props, ["component"]);
  return createDynamic(() => props.component, others);
}